import React, { Fragment, useEffect } from "react";
import { connect } from "react-redux";
import { bool, func, shape, arrayOf, number } from "prop-types";
import { routerActions } from "connected-react-router";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as checkActions, selectors as checkSelectors } from "reducers/checks";
import { actions as notificationActions } from "reducers/notification";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import CustomData from "pages/_components/CustomData/CustomData";
import { Col, Row } from "react-bootstrap";

import { Field, Form, Formik } from "formik";
import Selector from "pages/_components/fields/formik/Selector";
import TextField from "pages/_components/fields/TextField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import SelectorInput from "pages/_components/fields/formik/SelectorInput";
import { getAccountsWithChecksEnabled } from "util/checks";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import CustomProductHead from "pages/_components/CustomProductHead";
import GeneralMsg from "pages/_components/GeneralMsg";
import I18n from "pages/_components/I18n";

const NewCheckbookRequestForm = ({ dispatch, accounts, fetching, newCheckbookRequest, showlastValues }) => {
    const availableAccounts = getAccountsWithChecksEnabled(accounts);
    const isLoading = fetching || !newCheckbookRequest;
    const newCheckbookRequestFormData = {
        checksAccountSelector: !accounts?.length ? { options: [{ id: "" }] } : { options: availableAccounts },
        checksPerCheckbook: configUtils.getArray("checks.checkbook.checksPerCheckbook", ["25", "50"]),
        checkbookAmount: configUtils.getArray("checks.checkbook.checkbookAmount", [1, 2]),
        idTypes: configUtils.getArray("checks.checkbook.checkbookDocuments", ["DNI"]),
    };

    useEffect(() => {
        dispatch(accountsActions.listAccounts());
        return () => {
            dispatch(checkActions.setNewCheckbookShouldShowLastValues(false));
        };
    }, []);

    const FORM_ID = "NewCheckbookRequestForm";
    const REQUIRED_TEXT = i18n.get("deposits.request.required");
    const ALPHABETIC_TEXT_ONLY = i18n.get("checks.validation.alphabetic");
    const NUMBER_TEXT_ONLY = i18n.get("checks.validation.number");

    const fieldValidationSchemas = {
        firstName: Yup.string()
            .required(REQUIRED_TEXT)
            .matches(/[a-záéíóúüñçA-ZÁÉÍÓÚÜÑÇ]/, ALPHABETIC_TEXT_ONLY),
        lastName: Yup.string()
            .required(REQUIRED_TEXT)
            .matches(/[a-záéíóúüñçA-ZÁÉÍÓÚÜÑÇ]/, ALPHABETIC_TEXT_ONLY),
        retrieverId: Yup.object().shape({
            type: Yup.string(),
            code: Yup.string()
                .required(REQUIRED_TEXT)
                .matches(/^\d+$/, NUMBER_TEXT_ONLY),
        }),
    };

    const submitForm = async (event, allFormikProps) => {
        if (event) {
            event.preventDefault();
        }

        let allFieldsValids = true;
        const fields = Object.keys(fieldValidationSchemas);
        for (let i = 0; i < fields.length; i++) {
            const handleBlurRes = await handleBlur(fields[i], allFormikProps);
            if (handleBlurRes) {
                allFieldsValids = false;
            }
        }
        if (allFieldsValids) {
            allFormikProps.submitForm();
        }
    };

    const handleBlur = async (field, allFormikProps) => {
        allFormikProps.setFieldTouched(field, true);
        const fieldValidationRes = await validateField(field, allFormikProps.values);
        allFormikProps.setFieldError(field, fieldValidationRes);
        return fieldValidationRes;
    };

    const validateField = async (field, values) =>
        await fieldValidationSchemas[field]
            .validate(values[field], { context: values })
            .then((val) => null)
            .catch((err) => err.message);

    const handleChange = ({ type, code, setTouched, touched, setValues, values }) => {
        setTouched({ ...touched, retrieverId: true });
        setValues({ ...values, retrieverId: { type: type.value || values.retrieverId.type, code } });
    };

    const formikProps = {
        initialValues: {
            checksAccountSelector: showlastValues
                ? newCheckbookRequest?.checksAccountSelector || ""
                : newCheckbookRequestFormData.checksAccountSelector.options[0]?.id,
            checksPerCheckbook: showlastValues
                ? newCheckbookRequest?.checksPerCheckbook || ""
                : newCheckbookRequestFormData.checksPerCheckbook[0],
            checkbookAmount: showlastValues ? newCheckbookRequest?.checkbookAmount || "" : 1,
            retrieverId: showlastValues
                ? { type: newCheckbookRequest?.retrieverId?.type, code: newCheckbookRequest?.retrieverId?.code } || ""
                : { type: newCheckbookRequestFormData.idTypes[0], code: "" },
            firstName: showlastValues ? newCheckbookRequest?.firstName || "" : "",
            lastName: showlastValues ? newCheckbookRequest?.lastName || "" : "",
        },
        onSubmit: (newCheckData) => {
            const checksAccountSelected = newCheckbookRequestFormData?.checksAccountSelector.options.filter(
                (account) => account.id === newCheckData.checksAccountSelector,
            )[0];
            dispatch(checkActions.setNewCheckbookRequest({ checksAccountSelected, ...newCheckData }));
            dispatch(routerActions.push("/checks/summaryCheckbookForm"));
        },

        validateOnChange: false,
        validateOnBlur: false,
        enableReinitialize: true,
    };

    return (
        <Fragment>
            <Notification scopeToShow="checks" />
            <CustomProductHead titleId="checks.createCheckbook.title" backLinkTo="/desktop">
                <div className="toolbar-item view-title">
                    <ViewTitle id="checks.createCheckbook.title" />
                </div>
            </CustomProductHead>
            <MainContainer showLoader={isLoading}>
                <div className="container">
                    {availableAccounts.length === 0 ? (
                        <GeneralMsg
                            imagePath="images/coloredIcons/accounts.svg"
                            description={<I18n id="accounts.check.empty" />}
                        />
                    ) : (
                        <Formik {...formikProps}>
                            {(allFormikProps) => (
                                <Form id={FORM_ID} onSubmit={(e) => submitForm(e, allFormikProps)}>
                                    <Row className="form-background">
                                        <Col xs={12} md={8} mdOffset={2}>
                                            <Field
                                                idForm={FORM_ID}
                                                name="checksAccountSelector"
                                                label={i18n.get("checks.createCheckbook.account")}
                                                component={ProductSelector}
                                                data={newCheckbookRequestFormData.checksAccountSelector}
                                            />
                                        </Col>
                                        <Col xs={12} md={4} mdOffset={2}>
                                            <Field
                                                idForm={FORM_ID}
                                                name="checksPerCheckbook"
                                                label={i18n.get("checks.createCheckbook.amountPerCheckbook")}
                                                value={newCheckbookRequestFormData.checksPerCheckbook[0]}
                                                component={Selector}
                                                options={newCheckbookRequestFormData.checksPerCheckbook.map(
                                                    (value) => ({
                                                        value,
                                                        label: value,
                                                    }),
                                                )}
                                            />
                                        </Col>
                                        <Col xs={12} md={4} mdOffset={6}>
                                            <Field
                                                className="grid-table-data"
                                                idForm={FORM_ID}
                                                name="checkbookAmount"
                                                label={i18n.get("checks.createCheckbook.amountOfCheckbooks")}
                                                value={newCheckbookRequestFormData.checkbookAmount[0]}
                                                component={Selector}
                                                options={newCheckbookRequestFormData.checkbookAmount.map((value) => ({
                                                    value,
                                                    label: value,
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={12} md={8} mdOffset={2}>
                                            <h4>{i18n.get("checks.createCheckbook.authorizedRetriever")}</h4>
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={2}>
                                            <Field
                                                idForm={FORM_ID}
                                                name="retrieverId"
                                                label={i18n.get("checks.createCheckbook.retrieverId")}
                                                component={SelectorInput}
                                                onBlur={() => handleBlur("retrieverId", allFormikProps)}
                                                selectProps={{
                                                    name: "type",
                                                    value: allFormikProps.values.retrieverId.type,
                                                    options: newCheckbookRequestFormData.idTypes.map((value) => ({
                                                        value,
                                                        label: value,
                                                    })),
                                                }}
                                                inputProps={{
                                                    name: "code",
                                                    value: allFormikProps.values.retrieverId.code,
                                                }}
                                                onChange={(type, code) =>
                                                    handleChange({ type, code, ...allFormikProps })
                                                }
                                            />
                                        </Col>
                                        <Col xs={12} md={4} mdOffset={2}>
                                            <Field
                                                component={TextField}
                                                placeholder={i18n.get("checks.createCheckbook.firstName.placeholder")}
                                                idForm={FORM_ID}
                                                name="firstName"
                                                type="text"
                                                labelText="checks.createCheckbook.firstName"
                                                onBlur={() => handleBlur("firstName", allFormikProps)}
                                            />
                                        </Col>
                                        <Col xs={12} md={4} mdOffset={6}>
                                            <Field
                                                component={TextField}
                                                placeholder={i18n.get("checks.createCheckbook.lastName.placeholder")}
                                                idForm={FORM_ID}
                                                name="lastName"
                                                type="text"
                                                labelText="checks.createCheckbook.lastName"
                                                onBlur={() => handleBlur("lastName", allFormikProps)}
                                            />
                                        </Col>
                                        <Col xs={12} md={8} mdOffset={2}>
                                            <h4>{i18n.get("checks.createCheckbook.deliveryPoint.title")}</h4>
                                            <p>{i18n.get("checks.createCheckbook.deliveryPoint.address")}</p>
                                        </Col>
                                        <Col xs={12} md={8} mdOffset={2} className="my-7">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={i18n.get("checks.createCheckbook.deliveryPoint.info")}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="form-actions">
                                        <Col xs={12} md={3} mdOffset={2}>
                                            <CustomButton
                                                text={i18n.get("global.cancel")}
                                                func={() => dispatch(routerActions.push("/desktop"))}
                                                className="btn-outline flex"
                                            />
                                        </Col>
                                        <Col xs={12} md={3} mdOffset={8}>
                                            <CustomButton
                                                text={i18n.get("global.continue")}
                                                type="submit"
                                                className="btn-primary flex"
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    )}
                </div>
            </MainContainer>
        </Fragment>
    );
};

NewCheckbookRequestForm.propTypes = {
    newCheckbookRequest: shape({}).isRequired,
    accounts: arrayOf({ length: number.isRequired }),
    showlastValues: bool.isRequired,
    dispatch: func.isRequired,
    fetching: bool,
};

NewCheckbookRequestForm.defaultProps = {
    fetching: false,
    accounts: [],
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    newCheckbookRequest: checkSelectors.getNewCheckbookRequest(state),
    showlastValues: checkSelectors.getNewCheckbookShouldShowLastValues(state),
    fetching: accountsSelectors.getFetching(state),
});

export default connect(mapStateToProps)(NewCheckbookRequestForm);
